import { useEffect, useState, useRef } from "react";
import { useCurrentAccountId } from "@zapier/identity";
import { useExecuteTemplate } from "@zapier/templates";
import { useTemplateExecutionContext } from "src/contexts/TemplateExecutionContext";

const ALERT_DURATION = 10_000;

export const useExecuteTemplateOnMount = () => {
  const currentAccountId = useCurrentAccountId();
  const hasExecuted = useRef(false);

  const { executeTemplate, formatExecutionError } = useExecuteTemplate();

  const {
    creationScreen,
    setError,
    handleSignupRedirect,
    zapTemplateAttemptId,
    removeLocalStorageTemplateCreateKey,
    templateToCreate,
    localTemplateKey,
    setCreationScreen,
    setTemplateToCreate,
    shouldUseTemplateAPIExecute,
  } = useTemplateExecutionContext();

  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (hasExecuted.current) return;

    const url = new URL(window.location.href);
    const createQueryParam = url.searchParams.get("create") === "true";

    /**
     * If the user tries to create a template while logged out, we save that template in
     * localStorage, and upon arriving back here from the login/signup flow, we will
     * check for it and initiate the creation if the create=true query param is set.
     */
    const templateKey = templateToCreate ?? localTemplateKey;

    if (!templateKey) return;

    if (createQueryParam) {
      setCreationScreen(templateKey);
    }

    const shouldCreateTemplate = Boolean(
      !isCreating && (creationScreen || createQueryParam),
    );

    if (shouldCreateTemplate && !currentAccountId) {
      return handleSignupRedirect();
    }

    if (!isCreating && shouldCreateTemplate && currentAccountId) {
      hasExecuted.current = true;
      removeLocalStorageTemplateCreateKey();

      executeTemplate({
        templateSlug: templateKey,
        url: window.location.href,
        shouldUseTemplateAPIExecute,
      } as any)
        .catch((error) => {
          setError(formatExecutionError(error));
          setTimeout(() => setError(null), ALERT_DURATION);
          // Only set this to false if something goes wrong, because `handleCreate`
          // will redirect them after the template is executed otherwise.
        })
        .finally(() => {
          setIsCreating(false);
          setCreationScreen(null);
          hasExecuted.current = false;
        });

      setIsCreating(true);
      setTemplateToCreate(null);
    }
  }, [
    creationScreen,
    currentAccountId,
    executeTemplate,
    handleSignupRedirect,
    isCreating,
    localTemplateKey,
    removeLocalStorageTemplateCreateKey,
    setCreationScreen,
    setError,
    setTemplateToCreate,
    shouldUseTemplateAPIExecute,
    templateToCreate,
    zapTemplateAttemptId,
  ]);
};
